import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, errorMessage, setAdminEditionId } from "../lib/api";
import useNoindex from "../lib/useNoindex";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Users, Wallet, ShieldCheck, Trophy, AlertCircle, FileText } from "lucide-react";

import { StatCard, formatINR } from "../components/admin/shared";
import UsersPanel from "../components/admin/UsersPanel";
import TeamsPanel from "../components/admin/TeamsPanel";
import PrizesPanel from "../components/admin/PrizesPanel";
import JudgesPanel from "../components/admin/JudgesPanel";
import SubmissionsPanel from "../components/admin/SubmissionsPanel";
import ScoringPanel from "../components/admin/ScoringPanel";
import ResultsPanel from "../components/admin/ResultsPanel";
import StatusPanel from "../components/admin/StatusPanel";
import SponsorsPanel from "../components/admin/SponsorsPanel";
import WaitlistPanel from "../components/admin/WaitlistPanel";
import AdminsPanel from "../components/admin/AdminsPanel";
import PaymentsPanel from "../components/admin/PaymentsPanel";
import FunnelCard from "../components/admin/FunnelCard";

const TABS = [
  { v: "users", l: "Users" }, { v: "teams", l: "Teams" }, { v: "prizes", l: "Prizes" },
  { v: "judges", l: "Judges" }, { v: "submissions", l: "Submissions" }, { v: "scoring", l: "Scoring" },
  { v: "results", l: "Results" }, { v: "payments", l: "Payments" }, { v: "config", l: "Status" },
  { v: "sponsors", l: "Sponsors" }, { v: "waitlist", l: "Waitlist" }, { v: "admins", l: "Admins" },
];

export default function Admin() {
  useNoindex();
  const { user, loading } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editions, setEditions] = useState([]);
  const [editionId, setEditionId] = useState(null);
  const [activeEditionId, setActiveEditionId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, u, t] = await Promise.all([api.get("/admin/stats"), api.get("/admin/users"), api.get("/admin/teams")]);
      setStats(s.data); setUsers(u.data.users); setTeams(t.data.teams);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  }, []);

  const loadEditions = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/editions");
      setEditions(data.editions || []);
      setActiveEditionId(data.active_edition_id || null);
    } catch (err) { console.error("Failed to load editions:", err); /* non-fatal */ }
  }, []);

  useEffect(() => { if (user?.role === "admin") loadEditions(); }, [user, loadEditions]);
  useEffect(() => { if (user?.role === "admin") load(); }, [user, load, editionId]);

  const onEditionChange = (id) => {
    const next = id === activeEditionId ? null : id; // null => track active
    setAdminEditionId(next);
    setEditionId(id);
  };

  if (loading) return <div className="App"><Navbar /><div className="min-h-[60vh] flex items-center justify-center"><span className="spinner" /></div></div>;
  if (!user || user.role !== "admin") return <Navigate to="/login" replace />;

  const runDQ = async () => {
    if (!window.confirm("Force-run disqualification scan now?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/run-disqualification");
      toast.success(`Disqualified ${data.disqualified_teams ?? 0} team(s)`);
      await load();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  const tabCls = "rounded-none px-5 py-3 font-mono text-xs tracking-widest uppercase data-[state=active]:bg-brand data-[state=active]:text-black";

  return (
    <div className="App" data-testid="admin-page">
      <Navbar />
      <div className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12 py-10">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="label-mono mb-2">Admin Console</div>
            <h1 className="font-display text-4xl sm:text-5xl text-white">Operations <span className="text-brand">overview</span></h1>
          </div>
          <div className="flex items-end gap-3">
            {editions.length > 0 && (
              <div>
                <label className="label-mono block mb-1">Viewing edition</label>
                <select
                  value={editionId || activeEditionId || ""}
                  onChange={(e) => onEditionChange(e.target.value)}
                  className="input-field min-w-[220px]"
                  data-testid="edition-switcher"
                >
                  {editions.map((ed) => (
                    <option key={ed.id} value={ed.id}>
                      {`${ed.name} ${ed.edition}`.trim()}{ed.active ? " · ACTIVE" : ` · ${(ed.status || "").toUpperCase()}`}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <button onClick={runDQ} disabled={busy} className="btn-secondary flex items-center gap-2" data-testid="run-dq-btn">
              <AlertCircle className="w-4 h-4" strokeWidth={1.5} /> Run DQ Scan
            </button>
          </div>
        </div>

        {editionId && editionId !== activeEditionId && (
          <div className="mt-5 card-surface border-l-2 border-yellow-500 bg-yellow-500/5 flex items-center gap-3" data-testid="archived-edition-banner">
            <AlertCircle className="w-4 h-4 text-yellow-400 flex-shrink-0" strokeWidth={2} />
            <p className="text-sm text-gray-300">
              You are viewing a <span className="text-yellow-400 font-semibold">past edition</span> (read-only history). Switch back to the active edition to manage the live hackathon.
            </p>
          </div>
        )}

        {stats && (
          <div className="mt-8 grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="stats-grid">
            <StatCard icon={Users} label="Total Users" value={stats.total_users} testid="stat-users" />
            <StatCard icon={ShieldCheck} label="Verified" value={stats.verified_users} testid="stat-verified" />
            <StatCard icon={Wallet} label="Paid Users" value={stats.paid_users} accent testid="stat-paid" />
            <StatCard icon={Trophy} label="Revenue" value={formatINR(stats.payment_total)} accent testid="stat-revenue" />
            <StatCard icon={Users} label="Teams" value={stats.total_teams} testid="stat-teams" />
            <StatCard icon={ShieldCheck} label="Registered" value={stats.registered_teams} testid="stat-reg-teams" />
            <StatCard icon={AlertCircle} label="Forming" value={stats.forming_teams} testid="stat-forming" />
            <StatCard icon={AlertCircle} label="Disqualified" value={stats.disqualified_teams} testid="stat-dq" />
            <StatCard icon={FileText} label="R1 Submitted" value={stats.total_r1_submitted || 0} accent testid="stat-r1-submitted" />
          </div>
        )}

        {stats && <FunnelCard key={`funnel-${editionId || "active"}`} />}

        <Tabs defaultValue="users" className="mt-10" data-testid="admin-tabs" key={`tabs-${editionId || "active"}`}>
          <TabsList className="bg-[#111827] border border-white/10 rounded-none p-0 h-auto flex-wrap">
            {TABS.map(t => (
              <TabsTrigger key={t.v} value={t.v} className={tabCls} data-testid={`tab-${t.v}`}>{t.l}</TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="users" className="mt-4"><UsersPanel users={users} onChange={load} /></TabsContent>
          <TabsContent value="teams" className="mt-4"><TeamsPanel teams={teams} onChange={load} /></TabsContent>
          <TabsContent value="prizes" className="mt-4"><PrizesPanel /></TabsContent>
          <TabsContent value="judges" className="mt-4"><JudgesPanel /></TabsContent>
          <TabsContent value="submissions" className="mt-4"><SubmissionsPanel /></TabsContent>
          <TabsContent value="scoring" className="mt-4"><ScoringPanel /></TabsContent>
          <TabsContent value="results" className="mt-4"><ResultsPanel /></TabsContent>
          <TabsContent value="payments" className="mt-4"><PaymentsPanel /></TabsContent>
          <TabsContent value="config" className="mt-4"><StatusPanel /></TabsContent>
          <TabsContent value="sponsors" className="mt-4"><SponsorsPanel /></TabsContent>
          <TabsContent value="waitlist" className="mt-4"><WaitlistPanel /></TabsContent>
          <TabsContent value="admins" className="mt-4"><AdminsPanel /></TabsContent>
        </Tabs>
      </div>
      <Footer />
    </div>
  );
}
